import { TypedEventTarget } from '@halo/utils';
import type { QuickOptions, RoomOptions, RoomUser, UserMediaTypes } from 'quickvo-sdk-js';
import { QuickVO } from 'quickvo-sdk-js';
import { Logger } from '@halo/utils';
import type { MediaSDKDevices } from './types';
import { MediaSDKDeviceType, MediaSDKMediaType } from './types';

export interface MediaSDKOptions extends QuickOptions {}

export interface MediaSDKInitOptions {
  userId: string;
  deviceId: string;
}

export class MediaSDK extends TypedEventTarget {
  private getAuthFlag = false;
  quickvo: QuickVO;
  options: MediaSDKOptions;
  logger: Logger;

  constructor(options: MediaSDKOptions) {
    super();
    this.options = options;
  }

  init({ userId, deviceId }: MediaSDKInitOptions) {
    const { options } = this;

    this.logger = new Logger('MediaSDK', options.debug);
    this.quickvo = new QuickVO(options);
    this.initEvents();
    if (options.debug) {
      // @ts-expect-error debug
      window.mediaSDK = this;
    }
    this.logger.log('init', { userId, deviceId });
    const { quickvo } = this;

    quickvo.setOptions({
      userId: `${userId}.${deviceId}`,
    });

    quickvo.earlyConnect();
  }

  async joinRoom({
    media_types,
    ...options
  }: RoomOptions & { media_types: MediaSDKMediaType[] }) {
    const { quickvo, genSubscribeUserMediaTypes } = this;

    const logCb = this.logger.asyncLog('joinRoom', options);
    const members = (await quickvo.joinRoom(options)) as RoomUser[];
    logCb(members);

    const userMediaTypes = genSubscribeUserMediaTypes(members, [
      MediaSDKMediaType.MICROPHONECAMERA_AUDIO,
    ]);

    await this.subscribe(userMediaTypes);

    const logCbPublish = this.logger.asyncLog('publish', media_types);
    if (media_types.length) {
      const publish = await quickvo.publish(media_types).catch(console.log);
      logCbPublish(publish);
    }

    return members;
  }

  genSubscribeUserMediaTypes(members: RoomUser[], mediaTypes: MediaSDKMediaType[]) {
    return members?.flatMap((member) => {
      if (!member.isSelf) {
        return [{ userId: member.id, mediaTypes }];
      } else {
        return [];
      }
    });
  }

  async subscribe(userMediaTypes?: UserMediaTypes[]) {
    const { quickvo } = this;
    const logCb = this.logger.asyncLog('subscribe', userMediaTypes);
    const res = await quickvo.subscribe(userMediaTypes);
    logCb(true);
    return res;
  }

  async stopSubscribe(userMediaTypes?: UserMediaTypes[]) {
    const { quickvo } = this;
    const logCb = this.logger.asyncLog('stopSubscribe', userMediaTypes);
    await quickvo.stopSubscribe(userMediaTypes);
    logCb(true);
  }

  async getDevicesAuth(force = false): Promise<() => Promise<void> | void> {
    const { quickvo, getAuthFlag } = this;
    if (!getAuthFlag || force) {
      this.getAuthFlag = true;
      await quickvo
        ?.setLocalStream(
          [MediaSDKMediaType.MICROPHONECAMERA_AUDIO, MediaSDKMediaType.MICROPHONECAMERA_VIDEO],
          true
        )
        .catch((_) => {
          this.getAuthFlag = false;
        });
      return async () => {
        await quickvo?.setLocalStream(
          [MediaSDKMediaType.MICROPHONECAMERA_AUDIO, MediaSDKMediaType.MICROPHONECAMERA_VIDEO],
          false
        );
      };
    }
  }

  async getDevices(): Promise<MediaSDKDevices> {
    const logCb = this.logger.asyncLog('getDevices');

    const { quickvo } = this;

    const [audioinput, audiooutput, videoinput] = (
      await Promise.all(
        [
          MediaSDKDeviceType.AUDIO_INPUT,
          MediaSDKDeviceType.AUDIO_OUTPUT,
          MediaSDKDeviceType.VIDEO_INPUT,
        ].map((type) => quickvo.getEnumerateDevices(type))
      )
    ).map((devices) => devices.filter((device) => device.deviceId));

    logCb({ audioinput, audiooutput, videoinput });

    return {
      audioinput,
      audiooutput,
      videoinput,
    };
  }

  async getDevicesActivatedId(): Promise<Record<MediaSDKDeviceType, string>> {
    const logCb = this.logger.asyncLog('getDevicesActivatedId');

    // const closeAuth = await this.getDevicesAuth();

    const { quickvo } = this;
    const [audioinput, audiooutput, videoinput] = await Promise.all(
      [
        MediaSDKDeviceType.AUDIO_INPUT,
        MediaSDKDeviceType.AUDIO_OUTPUT,
        MediaSDKDeviceType.VIDEO_INPUT,
      ].map((type) => quickvo.getMediaDeviceKind(type))
    );

    logCb({ audioinput, audiooutput, videoinput });

    // await closeAuth?.();

    return {
      audioinput,
      audiooutput,
      videoinput,
    };
  }
  // TODO amo: Move to app
  async drawSpectrum(
    id: string,
    canvas: HTMLCanvasElement | undefined,
    frameId: string,
    onDrawBefrore?: () => Promise<boolean>,
    onDrawEnd?: () => void,
    onData?: (data: Uint8Array<ArrayBuffer>) => void
  ) {
    if (!canvas) return;
    const userAudioContext = this.quickvo.getUserAudioMediaStreamContext(
      id,
      MediaSDKMediaType.MICROPHONECAMERA_AUDIO
    );
    if (!userAudioContext && onDrawBefrore) {
      const resNext = await onDrawBefrore();
      if (!resNext) {
        return;
      }
    }

    const { analyserNode, analyserArrayData: array = new Uint8Array(255) } =
      userAudioContext ?? {};

    const cwidth = canvas.width;
    const cheight = canvas.height;
    const cpadding = 1;

    const meterWidth = 1.5;
    const meterGap = 1.5;
    const meterY = cheight / 2;
    const meterWidthWithGap = meterWidth + meterGap;
    const meterNum = Math.floor((cwidth - cpadding * 2 + meterGap) / meterWidthWithGap);
    //array as Uint8Array 0-255 128
    const meterRatio = cheight / 128;

    const ctx = canvas.getContext('2d')!;

    const draw = () => {
      onData?.(array as Uint8Array<ArrayBuffer>);
      analyserNode?.getByteFrequencyData(array as Uint8Array<ArrayBuffer>);
      const step = Math.round(array.length / meterNum);
      ctx.clearRect(0, 0, cwidth, cheight);

      for (let i = 0; i < meterNum; i++) {
        const value = array[i * step];
        const meterX = cpadding + meterGap / 2 + i * meterWidthWithGap;
        const meterHeight = value ? (value * meterRatio) / 2 : 2;

        ctx.fillStyle = '#43fff5';
        ctx.beginPath();
        ctx.roundRect(meterX, meterY, meterWidth, meterHeight, [0, 0, 2, 2]);
        ctx.roundRect(meterX, meterY, meterWidth, -meterHeight, [0, 0, 2, 2]);
        ctx.closePath();
        ctx.fill();
      }
    };
    this.quickvo?.addRequestAnimationFrame(frameId, draw);
    onDrawEnd?.();
  }

  initEvents() {
    const { quickvo, genSubscribeUserMediaTypes } = this;
    quickvo.addNotify({
      event: 'onPublish',
      callback: async (e) => {
        const userMediaTypes = genSubscribeUserMediaTypes(
          [e.data.user],
          [MediaSDKMediaType.MICROPHONECAMERA_AUDIO]
        );
        this.subscribe(userMediaTypes);
      },
    });
  }

  [Symbol.dispose](): void {
    const { quickvo, logger } = this;
    quickvo?.destroy();
    logger?.[Symbol.dispose]();
  }
}
